import React, { Key, useImperativeHandle, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChevronIcon } from '../../assets';
import { inMobile, useWindowDimensions } from '../../hooks/useDimensions';
import { recalculateMaxHeight } from '../../utils/Dropdown';
import { Checkbox, ShimmerImage, ShimmerText } from '..';
import { Radio } from '../';
import { DropdownItemProps, DropDownProps, getItemKey } from '../Dropdown/Dropdown';
import {
  BottomSheetItemText,
  Container,
  DropDownButton,
  DropDownButtonContent,
  DropdownError,
  DropDownItemContainer,
  DropDownItemDescription,
  DropDownItemText,
  DropDownMenuItem,
  StyledAvatar,
  StyledDropdown,
  Title,
} from '../Dropdown/Dropdown.styles';
import HelpTooltip from '../HelpTooltip';
import ActionModal, { ActionModalComponentProps } from '../Modal/ActionModal/ActionModal';
import { ShimmerItemContainer, StyledBottomSheetContainer, StyledDropDownMenu } from './InfinitScrollDropdown.styles';

interface ModalSelectionComponentProps extends ActionModalComponentProps {
  items: DropdownItemProps[];
  searchPlaceholder?: string;
  showMobileSearch?: boolean;
  selected: Set<Key>;
  disablePadding?: boolean;
  onRadioGroupChange?: (selection: Set<Key>, items?: DropdownItemProps[]) => void;
  maxHeight?: string;
  useGetItems?: (...props: any) => {
    items: DropdownItemProps[];
    hasMore: boolean;
    handleLoadMore: () => Promise<void>;
    loadingFirst: boolean;
    loadingMore: boolean;
  };
}

const ModalSelectionComponent = React.forwardRef(
  (
    {
      showMobileSearch,
      selected,
      disablePadding,
      onRadioGroupChange,
      onFinish,
      maxHeight,
      useGetItems,
    }: ModalSelectionComponentProps & InfiniteScrollProps,
    ref,
  ) => {
    const { items, hasMore, handleLoadMore, loadingFirst, loadingMore } = useGetItems();

    const [selectedLocal, setSelectedLocal] = React.useState(selected);
    const [first] = selectedLocal;

    const scrollViewId = 'scrollableDiv';

    useImperativeHandle(ref, () => ({
      submit: () => {
        onFinish?.();
      },
    }));

    const handleRadioGroupChange = (value) => {
      setSelectedLocal(new Set<Key>([value]));
      onRadioGroupChange?.(value, items);
    };

    return (
      <>
        <InfiniteScroll
          dataLength={items.length}
          next={() => handleLoadMore()}
          hasMore={hasMore}
          hasChildren
          scrollThreshold="40px"
          scrollableTarget={scrollViewId}
          loader={null}
        >
          <StyledBottomSheetContainer
            $isSearchEnabled={showMobileSearch}
            $disablePadding={disablePadding}
            id={scrollViewId}
            $maxHeight={maxHeight}
          >
            <Radio.Group value={first} onChange={handleRadioGroupChange} aria-label="modalRadioGroup">
              {items.map((item, index) => (
                <Radio size="lg" style={{ flex: 1 }} value={item.text} key={`radioItem_${index}`}>
                  <BottomSheetItemText $isSelected={selectedLocal.has(getItemKey(item))}>
                    {item.text}
                  </BottomSheetItemText>
                </Radio>
              ))}
            </Radio.Group>
            {(loadingFirst || loadingMore) && mobileListShimmer}
          </StyledBottomSheetContainer>
        </InfiniteScroll>
      </>
    );
  },
);

export interface InfiniteScrollProps {
  hasMore: boolean;
  loadMore: () => void;
  loadingFirst?: boolean;
  loadingMore?: boolean;
  getItemsHook?: (...props: any) => {
    items: DropdownItemProps[];
    hasMore: boolean;
    handleLoadMore: () => Promise<void>;
    loadingFirst: boolean;
    loadingMore: boolean;
  };
}

const mobileListShimmer = [...Array(2)].map((_, index) => (
  <ShimmerItemContainer key={`loader-${index.toString()}`}>
    <ShimmerText $width="12rem" />
    <ShimmerImage $width="1.5rem" $height="1.5rem" $borderRadius="50%" />
  </ShimmerItemContainer>
));

const listShimmer = [...Array(2)].map((_, index) => (
  <DropDownMenuItem key={`loader-${index.toString()}`}>
    <DropDownItemContainer>
      <DropDownItemText>
        <ShimmerText $width="12rem" />
      </DropDownItemText>
    </DropDownItemContainer>
  </DropDownMenuItem>
));

export type InfiniteScrollDropDownProps = Omit<DropDownProps, 'onSelectionChange'> & {
  onSelectionChange: (selection: number | number[], updatedItems?: DropdownItemProps[]) => void;
};

const InfiniteScrollDropdown = ({
  items,
  alwaysShowTitle,
  title,
  titleColor,
  mobileModalTitle,
  selectedIndex,
  placeholder,
  multiselect,
  helpText,
  disabled,
  onSelectionChange,
  bordered = true,
  placement = 'bottom-left',
  buttonSidesPadding,
  disallowEmptySelection,
  itemTemplate,
  disabledKeys,
  minWidthButton,
  staticMenuWidth,
  buttonFontSize,
  buttonFontWeight,
  noPaddingLeft,
  expandWithoutBorder,
  buttonMaxHeight,
  fullWidth,
  buttonCSS,
  displayRadioIcon = true,
  menuItemTextCSS,
  menuItemCSS,
  menuContainerCSS,
  noIconRight,
  iconLeft,
  fixedButtonLabel,
  disableSvgAnimation,
  showMobileSearch,
  searchPlaceholder,
  error,
  zIndexBottomSheet,
  customBottomSheetHandler,
  loadMore,
  hasMore,
  loadingFirst,
  loadingMore,
  getItemsHook,
  ...rest
}: InfiniteScrollDropDownProps & InfiniteScrollProps) => {
  const [selected, setSelected] = React.useState(
    new Set<Key>(selectedIndex !== undefined ? [getItemKey(items?.[selectedIndex])] : []),
  );
  const [isOpen, setIsOpen] = React.useState(false);
  const { width: windowWidth } = useWindowDimensions();
  const [maxHeight, setMaxHeight] = useState(!inMobile(windowWidth) ? '20rem' : undefined);

  const scrollViewId = 'scrollableDiv';

  const [selectedIndexInitial, setSelectedIndexInitial] = useState(selectedIndex);
  if (selectedIndex !== undefined && selectedIndex !== selectedIndexInitial) {
    setSelectedIndexInitial(selectedIndex);
    setSelected(new Set<Key>([getItemKey(items?.[selectedIndex])]));
  }

  // Reset values if selectedIndex is undefined
  React.useEffect(() => {
    if (selectedIndex === undefined) {
      setSelected(new Set<Key>([]));
    }
  }, [selectedIndex]);

  const buttonRef = useRef<HTMLElement>();
  const [buttonWidth, setButtonWidth] = useState(0);
  const mobileModalRef = useRef(null);

  const selectedValue = React.useMemo(
    () =>
      Array.from(selected)
        .map((key) => items.find((item) => getItemKey(item) === key)?.text)
        .join(', '),
    [items, selected],
  );

  React.useLayoutEffect(() => {
    function updateSize() {
      setButtonWidth(buttonRef.current.offsetWidth);
    }

    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  const modalSelectionComponentProps = {
    items,
    hasMore,
    loadMore,
    searchPlaceholder,
    showMobileSearch,
    selected,
    onRadioGroupChange: (value, updatedItems?) => {
      setSelected(new Set<Key>([value]));
      const selectionItems = updatedItems ?? items;
      const selectedIndexes = Array.from([value] || []).map((key) =>
        selectionItems.findIndex((item) => item.text === key),
      );
      onSelectionChange?.(multiselect ? selectedIndexes : selectedIndexes[0], updatedItems);
      mobileModalRef.current?.close();
    },
    maxHeight: customBottomSheetHandler ? '25rem' : maxHeight, //TODO: calculate maxHeight in multistep modals
    useGetItems: getItemsHook,
  };

  const onDropdownButtonPressed = () => {
    if (inMobile(windowWidth)) {
      if (customBottomSheetHandler) {
        // timeout to avoid UI issues related to useRadioGroup hook
        setTimeout(() => {
          customBottomSheetHandler?.(<ModalSelectionComponent disablePadding {...modalSelectionComponentProps} />);
        }, 100);
      } else {
        mobileModalRef.current?.open();
        if (!maxHeight) {
          setTimeout(() => {
            setMaxHeight(recalculateMaxHeight(true));
          }, 100);
        }
      }
    }
  };

  const dropDownComponent = useMemo(() => {
    const onLocalSelectionChange = (keys: 'all' | Set<Key>) => {
      if (keys === 'all') return;
      if (keys.size === 0 && !multiselect) return;
      setSelected(keys);
      const selectedIndexes = Array.from(keys || []).map((key) => items.findIndex((item) => getItemKey(item) === key));
      onSelectionChange?.(multiselect ? selectedIndexes : selectedIndexes[0]);
    };
    return (
      <StyledDropDownMenu
        id={scrollViewId}
        $maxHeight={maxHeight}
        width={staticMenuWidth ? undefined : buttonWidth}
        selectionMode={multiselect ? 'multiple' : 'single'}
        disallowEmptySelection={multiselect ? false : disallowEmptySelection}
        selectedKeys={selected}
        disabledKeys={disabledKeys}
        onSelectionChange={onLocalSelectionChange}
        menuContainerCSS={menuContainerCSS}
      >
        {items
          .map((item, index) => (
            <DropDownMenuItem css={menuItemCSS} key={getItemKey(item)} textValue={item.text} dividerWeight="extrabold">
              {itemTemplate ? (
                <>{itemTemplate(item)}</>
              ) : (
                <DropDownItemContainer>
                  {multiselect ? (
                    <Checkbox isSelected={selected.has(getItemKey(item))} aria-label={`desktopCheckbox_${index}`} />
                  ) : (
                    <>
                      {displayRadioIcon ? (
                        <Radio.Group
                          value={selected.has(getItemKey(item)) ? item.text : undefined}
                          aria-label="desktopRadioGroup"
                        >
                          <Radio value={item.text} aria-label={`desktopRadio_${index}`} />
                        </Radio.Group>
                      ) : null}
                    </>
                  )}
                  <DropDownItemText $isSelected={selected.has(getItemKey(item))} css={menuItemTextCSS}>
                    {item.text}
                  </DropDownItemText>
                  {item.description && <DropDownItemDescription>{item.description}</DropDownItemDescription>}
                </DropDownItemContainer>
              )}
            </DropDownMenuItem>
          ))
          .concat((loadingFirst || loadingMore) && listShimmer)}
      </StyledDropDownMenu>
    );
  }, [
    buttonWidth,
    disabledKeys,
    disallowEmptySelection,
    displayRadioIcon,
    itemTemplate,
    items,
    loadingFirst,
    loadingMore,
    maxHeight,
    menuContainerCSS,
    menuItemCSS,
    menuItemTextCSS,
    multiselect,
    onSelectionChange,
    selected,
    staticMenuWidth,
  ]);
  const infiniteScrollComponent = useMemo(() => {
    return (
      <>
        <InfiniteScroll
          dataLength={items.length}
          next={() => loadMore()}
          hasMore={hasMore}
          scrollableTarget={scrollViewId}
          scrollThreshold="0px"
          loader={null}
        >
          {dropDownComponent}
        </InfiniteScroll>
      </>
    );
  }, [items.length, hasMore, loadMore, dropDownComponent]);

  //This effect will ensure selected item is updated when used in multistep modals
  React.useEffect(() => {
    setSelected(new Set<Key>(selectedIndex !== undefined ? [getItemKey(items?.[selectedIndex])] : []));
  }, [items, selectedIndex]);

  return (
    <Container $fullWidth={fullWidth} {...rest}>
      {(title || alwaysShowTitle) && (
        <Title $alwaysShowTitle={alwaysShowTitle} $color={titleColor}>
          {title || ' '}
          {helpText && <HelpTooltip $helpText={helpText} />}
        </Title>
      )}
      <StyledDropdown
        isDisabled={disabled}
        placement={placement}
        onOpenChange={(isOpen) => {
          setIsOpen(isOpen);
          if (isOpen && !inMobile(windowWidth)) {
            setTimeout(() => {
              setMaxHeight(recalculateMaxHeight(false));
            }, 100);
          }
        }}
        error={error}
        {...rest}
      >
        <DropDownButton
          className="ddbutton"
          disabled={disabled}
          ref={buttonRef}
          iconRight={noIconRight ? undefined : <ChevronIcon />}
          buttonWidth={minWidthButton ? buttonWidth : 'unset'}
          bordered={bordered}
          buttonSidesPadding={buttonSidesPadding}
          fontSize={buttonFontSize}
          fontWeight={buttonFontWeight}
          noPaddingLeft={noPaddingLeft}
          expandWithoutBorder={expandWithoutBorder}
          maxHeight={buttonMaxHeight}
          buttonCSS={buttonCSS}
          onPress={onDropdownButtonPressed}
          $isOpen={isOpen}
          $disableSvgAnimation={disableSvgAnimation}
          color={error ? 'error' : 'default'}
          error={error}
        >
          <DropDownButtonContent>
            {/* If not multiselect, we give the option to show an avatar at the left of the selected option. */}
            {!multiselect && items.find((item) => getItemKey(item) === selected?.values()?.next()?.value)?.imageUrl && (
              <StyledAvatar src={items.find((item) => getItemKey(item) === selected.values().next().value).imageUrl} />
            )}
            {iconLeft}
            {fixedButtonLabel ?? (selectedValue || placeholder || '')}
          </DropDownButtonContent>
        </DropDownButton>
        {!inMobile(windowWidth) ? infiniteScrollComponent : <></>}
      </StyledDropdown>
      {error && <DropdownError>{error}</DropdownError>}
      <ActionModal
        ref={mobileModalRef}
        title={mobileModalTitle}
        Component={ModalSelectionComponent}
        componentProps={modalSelectionComponentProps}
        hideFooter
        noPadding
        zIndexBottomSheet={zIndexBottomSheet}
      />
    </Container>
  );
};

export default InfiniteScrollDropdown;
